import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  Box,
  Stack,
  Typography,
  Button,
  Alert,
  Dialog,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { AppContext, AuthContext } from 'Context';
import { axios, endpoints, baseUrl } from 'Api';
import { logAppEvent } from 'Api/errorLogger';
import { setDidCardAdded } from 'store/store';
import CardDetails from 'Pages/private/settings/manage-payment-methods/CardDetails';
import AddCard from 'Pages/private/settings/manage-payment-methods/AddCard/AddCard';
import { InfoModal } from 'Pages/private/dashboard/info-modal';
import { V2SettingsShell } from '../../_shared/V2SettingsShell';

export const ManagePaymentMethodsV2 = () => {
  const {
    subscriptionDetails,
    langText,
    setAppSnackbar,
    setcardAdded,
    getSubscription,
    setcardUpdateBoolean,
    setcardPayId,
  } = useContext(AppContext);
  const { isInActiveUser, getToken } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const didCardAdded = useSelector((state) => state.didCardAdded);

  const [open, setOpen] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [renewingPlan, setRenewingPlan] = useState(false);

  const paymentMethodText = langText?.settings?.billPayment?.paymentMethod;
  const lastPlanId = subscriptionDetails?.plans?.[subscriptionDetails?.plans?.length - 1]?.plan_id;

  useLayoutEffect(() => {
    if (searchParams.get('add') === 'true') setOpen(true);
  }, [searchParams]);

  useEffect(() => {
    if (subscriptionDetails?.paymentMethods) {
      setPaymentMethods(subscriptionDetails.paymentMethods);
    }
  }, [subscriptionDetails]);

  const handleClose = () => setOpen(false);
  const handleUpdateState = () => dispatch(setDidCardAdded(false));

  const renewPlan = async () => {
    setRenewingPlan(true);
    try {
      const myToken = await getToken();
      const apiUrl = `${baseUrl}/${endpoints.addPlanV2}`;
      const response = await axios.post(apiUrl, {
        token: myToken,
        plan_id: lastPlanId,
        is_free: 0,
        upgrade: false,
        trial: false,
      });
      setAppSnackbar({ isVisible: true, type: 'success', message: response?.data?.message || 'Plan renewed successfully' });
      handleUpdateState();
      getSubscription();
      navigate('/settings/billings-and-payments');
      window.location.reload();
    } catch (error) {
      const responseData = error?.response?.data?.message;
      logAppEvent({
        userName: `${localStorage.getItem('firstname') || ''} ${localStorage.getItem('lastname') || ''}`.trim() || 'Guest',
        functionAffected: 'Purchase Plan',
        description: 'Plan purchase failed from manage payment methods',
        severity: 'high',
        errorMessage: responseData || error?.message || 'Plan purchase failed',
        responseStatus: error?.response?.status || null,
        responseBody: responseData || null,
        requestUrl: `${baseUrl}/${endpoints.addPlanV2}`,
        requestMethod: 'POST',
        component: 'ManagePaymentMethodsV2',
        userAction: 'Purchase plan called',
      });
      setAppSnackbar({
        isVisible: true,
        type: 'error',
        message: responseData || 'Oops! Something went wrong while renewing plan',
      });
    } finally {
      setRenewingPlan(false);
    }
  };

  const handleAddNewCard = () => {
    setcardUpdateBoolean(false);
    setcardAdded(false);
    setOpen(true);
  };

  const handleUpdateCard = (card, id) => {
    setcardUpdateBoolean(true);
    setcardPayId({ card, id });
    setcardAdded(false);
    setOpen(true);
  };

  return (
    <>
      <V2SettingsShell
        fitViewport
        hideQuickLinks
        title={paymentMethodText?.title || 'Manage payment methods'}
        description={paymentMethodText?.description || 'Edit your payment details, add a backup or switch your preferred payment method.'}
      >
        {didCardAdded === true && isInActiveUser && (
          <InfoModal
            isVisible={didCardAdded}
            title="Your subscription needs to be renewed"
            content={(
              <pre>
                Projector subscription was paused as your renewal with your
                {'\n'}
                payment method on file was unsuccessful.
                {'\n'}
                Do you wish to renew now?
              </pre>
            )}
            onHide={handleUpdateState}
            continueButton="Not now"
            action={renewPlan}
            actionButton={renewingPlan ? 'Renewing…' : 'Renew'}
            isDelete
            fromPayment="true"
          />
        )}

        <Stack spacing={3}>
          <Card sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={2}>
              {paymentMethods?.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  <Typography variant="body2">No payment methods on file yet.</Typography>
                </Box>
              )}
              {paymentMethods?.map(({ card, id, last_attempt }) => (
                <Box
                  key={id}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 3,
                    p: { xs: 1, md: 2 },
                  }}
                >
                  <CardDetails
                    paymentMethods={paymentMethods}
                    expMonth={card?.exp_month}
                    expyear={card?.exp_year}
                    brand={card.brand}
                    last4={card.last4}
                    cardId={id}
                    card={card}
                    handleUpdateCard={handleUpdateCard}
                    status={last_attempt}
                    removedAllowed={subscriptionDetails?.paymentMethods?.length > 1}
                    isPreferred={subscriptionDetails?.default_payment_methods === id}
                  />
                </Box>
              ))}
              {subscriptionDetails?.paymentMethods?.length <= 1 && (
                <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                  {paymentMethodText?.removeValidation || 'To delete a payment method, add a new one first.'}
                </Alert>
              )}
            </Stack>
          </Card>

          <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddRoundedIcon />}
              onClick={handleAddNewCard}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {paymentMethodText?.addMethod || 'Add new payment method'}
            </Button>
          </Box>
        </Stack>
      </V2SettingsShell>

      <Dialog
        fullScreen
        open={open}
        onClose={handleClose}
        aria-labelledby="responsive-dialog-title"
      >
        <AddCard closeAddCard={handleClose} />
      </Dialog>
    </>
  );
};
